/**
 * Collection Slider Component
 * Handles the collections slider functionality with touch/drag support
 */
class CollectionSlider {
  constructor(element) {
    this.slider = element;
    this.track = this.slider.querySelector('[data-slider-track]');
    this.prevBtn = this.slider.querySelector('[data-slider-prev]');
    this.nextBtn = this.slider.querySelector('[data-slider-next]');
    this.slides = this.slider.querySelectorAll('.collections-slider__slide');

    // State
    this.currentIndex = 0;
    this.slidesToShow = this.getSlidesToShow();
    this.slidesToScroll = this.getSlidesToScroll();
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.initialTransform = 0;
    this.dragStarted = false;
    this.dragThreshold = 5;

    this.init();
  }

  getSlidesToShow() {
    if (window.innerWidth >= 1200) return 4;
    if (window.innerWidth >= 992) return 3;
    if (window.innerWidth >= 768) return 3.5;
    if (window.innerWidth >= 576) return 3.25;
    return 2.2;
  }

  getSlidesToScroll() {
    if (window.innerWidth >= 1200) return 2;
    if (window.innerWidth >= 992) return 2;
    if (window.innerWidth >= 576) return 2;
    return 1;
  }

  getMaxIndex() {
    return Math.max(0, this.slides.length - Math.floor(this.slidesToShow));
  }

  updateNavigationVisibility() {
    const maxIndex = this.getMaxIndex();
    const needsNavigation = maxIndex > 0 && window.innerWidth >= 992; // Only show on desktop

    if (this.prevBtn) {
      this.prevBtn.style.display = needsNavigation ? 'flex' : 'none';
    }
    if (this.nextBtn) {
      this.nextBtn.style.display = needsNavigation ? 'flex' : 'none';
    }
  }

  updateSlider(animate = true) {
    const slideWidth = 100 / this.slidesToShow;
    const maxIndex = this.getMaxIndex();

    this.currentIndex = Math.min(Math.max(0, this.currentIndex), maxIndex);

    const translateX = -(this.currentIndex * slideWidth);

    if (animate) {
      this.track.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    } else {
      this.track.style.transition = 'none';
    }

    this.track.style.transform = `translateX(${translateX}%)`;

    // Update button states
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
      this.nextBtn.style.opacity = this.currentIndex >= maxIndex ? '0.3' : '1';
    }

    // Update navigation visibility
    this.updateNavigationVisibility();
  }

  goToPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex = Math.max(0, this.currentIndex - this.slidesToScroll);
      this.updateSlider();
    }
  }

  goToNext() {
    const maxIndex = this.getMaxIndex();
    if (this.currentIndex < maxIndex) {
      this.currentIndex = Math.min(maxIndex, this.currentIndex + this.slidesToScroll);
      this.updateSlider();
    }
  }

  handleStart(e) {
    this.isDragging = true;
    this.dragStarted = false;
    this.startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    this.currentX = this.startX;

    const transform = this.track.style.transform;
    this.initialTransform = transform ? parseFloat(transform.match(/-?\d+\.?\d*/)) || 0 : 0;

    this.track.style.transition = 'none';
    this.track.classList.add('dragging');

    // Prevent default to stop scrolling on touch
    if (e.type === 'touchstart') {
      e.preventDefault();
    }
  }

  handleMove(e) {
    if (!this.isDragging) return;

    this.currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const deltaX = this.currentX - this.startX;

    // Only start dragging if moved more than 5px
    if (!this.dragStarted && Math.abs(deltaX) > this.dragThreshold) {
      this.dragStarted = true;
      this.track.classList.add('drag-started');
    }

    if (this.dragStarted) {
      e.preventDefault();
      const containerWidth = this.slider.offsetWidth;
      const deltaPercent = (deltaX / containerWidth) * 100;

      this.track.style.transform = `translateX(${this.initialTransform + deltaPercent}%)`;
    }
  }

  handleEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('dragging');

    const deltaX = this.currentX - this.startX;
    const threshold = 60; // Minimum drag distance

    if (this.dragStarted && Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && this.currentIndex > 0) {
        this.goToPrev();
      } else if (deltaX < 0 && this.currentIndex < this.getMaxIndex()) {
        this.goToNext();
      } else {
        this.updateSlider();
      }
    } else {
      this.updateSlider();
    }

    // Reset drag state
    this.dragStarted = false;
    this.track.classList.remove('drag-started');
  }

  handleResize() {
    this.slidesToShow = this.getSlidesToShow();
    this.slidesToScroll = this.getSlidesToScroll();
    this.updateSlider();
    this.updateNavigationVisibility();
  }

  bindEvents() {
    // Navigation buttons
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goToPrev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goToNext());

    // Touch events with better handling
    this.track.addEventListener('touchstart', (e) => this.handleStart(e), {passive: false});
    this.track.addEventListener('touchmove', (e) => this.handleMove(e), {passive: false});
    this.track.addEventListener('touchend', (e) => this.handleEnd(e), {passive: true});

    // Mouse events
    this.track.addEventListener('mousedown', (e) => this.handleStart(e));
    document.addEventListener('mousemove', (e) => this.handleMove(e));
    document.addEventListener('mouseup', (e) => this.handleEnd(e));

    // Prevent context menu on long press
    this.track.addEventListener('contextmenu', (e) => e.preventDefault());

    // Handle resize with debouncing
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 150);
    });
  }

  init() {
    if (!this.track || this.slides.length === 0) {
      console.log('DEBUG: Missing track or no slides');
      return;
    }

    this.bindEvents();
    this.updateSlider();

    console.log('Collections slider initialized:', {
      slidesCount: this.slides.length,
      slidesToShow: this.slidesToShow,
      maxIndex: this.getMaxIndex(),
    });
  }
}

// Initialize all collection sliders on page load
document.addEventListener('DOMContentLoaded', function () {
  const sliders = document.querySelectorAll('[data-collections-slider]');

  sliders.forEach((slider) => {
    new CollectionSlider(slider);
  });
});

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionSlider;
}
